import { useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../../components/common/Icon';
import { compassWord, formatAreaDistance, turnDeg, wrap360 } from '../../utils/tourAreas';
import styles from './TourHorizon.module.css';

// Concept 1 of the Advanced Virtual Tour: every other area as a beacon in its
// real direction, chips at the screen edge for the ones out of view, and a
// compass band that shows all of them, the way round.

export interface HorizonArea {
  key: string;
  name: string;
  colour: string;
  scans: number;
  // On the ground, to the area's nearest scan.
  distance: number;
  // Compass bearing to that scan.
  bearing: number;
  photo?: string;
}

interface Props {
  areas: HorizonArea[];
  // Compass bearing of the view's centre and the horizontal field of view.
  viewBearing: number;
  hFov: number;
  // Where an area's beacon meets the horizon on screen, null when behind.
  place: (key: string) => { x: number; y: number } | null;
  onGo: (key: string) => void;
  // Turn the view to face an area.
  onFace: (key: string) => void;
}

const BAND_DEG = 360;
const BAND_TICK = 15;
const LETTERS: Record<number, string> = { 0: 'N', 45: 'NE', 90: 'E', 135: 'SE', 180: 'S', 225: 'SW', 270: 'W', 315: 'NW' };
const BEHIND_DEG = 135;
// The pill's widest (its max-width in the stylesheet).
const PILL_W = 260;
const CARD_W = 300;
const ROW_H = 46;
const EDGE_MAX = 3;

// Which compass items keep their distance label: left to right, a label only
// where it clears the one before; the rest show their dot alone.
export function bandLabels(items: Array<{ key: string; x: number; text: string }>): Set<string> {
  const keep = new Set<string>();
  let end = -Infinity;
  for (const it of [...items].sort((a, b) => a.x - b.x)) {
    const from = it.x - 9, to = from + 22 + it.text.length * 6.4;
    const covers = items.some((o) => o.key !== it.key && o.x + 6 > from && o.x - 6 < to);
    if (from < end + 6 || covers) continue;
    keep.add(it.key);
    end = to;
  }
  return keep;
}

export function stackRows(xs: Array<{ key: string; x: number }>, width = PILL_W): Map<string, number> {
  const rows: number[] = [];
  const out = new Map<string, number>();
  for (const it of [...xs].sort((a, b) => a.x - b.x)) {
    let row = rows.findIndex((right) => it.x - width / 2 >= right + 8);
    if (row < 0) { row = rows.length; rows.push(-Infinity); }
    rows[row] = it.x + width / 2;
    out.set(it.key, row);
  }
  return out;
}

const TourHorizon = ({ areas, viewBearing, hFov, place, onGo, onFace }: Props) => {
  const [open, setOpen] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const bandRef = useRef<HTMLDivElement>(null);
  const [bandW, setBandW] = useState(600);
  const [rootW, setRootW] = useState(0);
  useEffect(() => {
    const band = bandRef.current, root = rootRef.current;
    if (!band || !root || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => { setBandW(band.clientWidth || 600); setRootW(root.clientWidth); });
    ro.observe(band);
    ro.observe(root);
    return () => ro.disconnect();
  }, []);
  const half = hFov / 2;

  const sorted = useMemo(
    () => areas.map((a) => ({ ...a, turn: turnDeg(viewBearing, a.bearing) })),
    [areas, viewBearing],
  );
  const inView = sorted
    .filter((a) => Math.abs(a.turn) < half)
    .map((a) => ({ a, at: place(a.key) }))
    .filter((v): v is { a: typeof v.a; at: { x: number; y: number } } => !!v.at);
  const rows = stackRows(inView.map((v) => ({ key: v.a.key, x: v.at.x })));
  const left = sorted.filter((a) => a.turn <= -half && a.turn >= -BEHIND_DEG).sort((p, q) => q.turn - p.turn);
  const right = sorted.filter((a) => a.turn >= half && a.turn <= BEHIND_DEG).sort((p, q) => p.turn - q.turn);
  const behind = sorted.filter((a) => Math.abs(a.turn) > BEHIND_DEG).sort((p, q) => p.distance - q.distance);

  const labelled = bandLabels(sorted.map((a) => ({
    key: a.key, x: (0.5 + a.turn / BAND_DEG) * bandW, text: formatAreaDistance(a.distance),
  })));
  const ticks: Array<{ deg: number; x: number }> = [];
  const first = Math.ceil((viewBearing - BAND_DEG / 2) / BAND_TICK) * BAND_TICK;
  for (let d = first; d <= viewBearing + BAND_DEG / 2; d += BAND_TICK) {
    ticks.push({ deg: wrap360(d), x: 50 + ((d - viewBearing) / BAND_DEG) * 100 });
  }

  const chip = (a: (typeof sorted)[number], side: 'left' | 'right' | 'behind') => (
    <button
      key={a.key}
      type="button"
      className={styles.chip}
      onClick={() => onFace(a.key)}
      aria-label={`Turn to ${a.name}, ${formatAreaDistance(a.distance)}${side === 'behind' ? ' behind you' : ''}`}
    >
      {side === 'left' && <Icon name="chevron_left" className={styles.chipArrow} />}
      {side === 'behind' && <Icon name={a.turn < 0 ? 'rotate_left' : 'rotate_right'} className={styles.chipArrow} />}
      <span className={styles.dot} style={{ background: a.colour }} />
      <span className={styles.chipName}>{a.name}</span>
      <span className={styles.chipDist}>{formatAreaDistance(a.distance)}{side === 'behind' ? ' behind you' : ''}</span>
      {side === 'right' && <Icon name="chevron_right" className={styles.chipArrow} />}
    </button>
  );
  const more = (list: typeof sorted) => list.length > EDGE_MAX && (
    <span className={styles.more} title={list.slice(EDGE_MAX).map((a) => a.name).join(', ')}>+{list.length - EDGE_MAX} more</span>
  );

  return (
    <div ref={rootRef} className={styles.horizon}>
      <div ref={bandRef} className={styles.band} role="group" aria-label="Compass">
        <span className={styles.bandCaret} aria-hidden="true" />
        {ticks.map((t) => (
          <span key={`${t.deg}-${t.x.toFixed(1)}`} className={styles.tick} style={{ left: `${t.x}%` }}>
            <span className={LETTERS[t.deg]?.length === 1 ? styles.tickMajor : LETTERS[t.deg] ? styles.tickMid : styles.tickMinor} />
            {LETTERS[t.deg] && <span className={LETTERS[t.deg].length === 1 ? styles.letter : styles.letterSmall}>{LETTERS[t.deg]}</span>}
          </span>
        ))}
        {sorted.map((a) => (
          <button
            key={a.key}
            type="button"
            className={styles.bandArea}
            style={{ left: `${50 + (a.turn / BAND_DEG) * 100}%` }}
            onClick={() => onFace(a.key)}
            title={`${a.name}, ${formatAreaDistance(a.distance)} ${compassWord(a.bearing)}`}
            aria-label={`Turn to ${a.name}, ${formatAreaDistance(a.distance)} ${compassWord(a.bearing)}`}
          >
            <span className={styles.dot} style={{ background: a.colour }} />
            {labelled.has(a.key) && <span className={styles.bandDist}>{formatAreaDistance(a.distance)}</span>}
          </button>
        ))}
      </div>

      {inView.map(({ a, at }) => {
        const row = rows.get(a.key) ?? 0;
        const isOpen = open === a.key;
        // Near the screen's edge the pill or card slides inward; the stem stays put.
        const halfW = (isOpen ? CARD_W : PILL_W) / 2 + 8;
        const shift = rootW > 2 * halfW ? Math.min(Math.max(at.x, halfW), rootW - halfW) - at.x : 0;
        return (
          <div
            key={a.key}
            className={`${styles.beacon} ${isOpen ? styles.beaconOpen : ''}`}
            style={{ left: at.x, top: at.y, ['--rise' as string]: `${70 + row * ROW_H}px`, ['--shift' as string]: `${shift}px` }}
            onMouseEnter={() => setOpen(a.key)}
            onMouseLeave={() => setOpen((k) => (k === a.key ? null : k))}
          >
            <span className={styles.stem} style={{ borderColor: a.colour }} />
            <span className={styles.foot} style={{ background: a.colour }} />
            <div className={styles.beaconBody}>
              {isOpen ? (
                <div className={styles.card} role="dialog" aria-label={a.name}>
                  {a.photo && <img className={styles.photo} src={a.photo} alt="" loading="lazy" crossOrigin="anonymous" draggable={false} />}
                  <div className={styles.cardText}>
                    <span className={styles.cardName}><span className={styles.dot} style={{ background: a.colour }} />{a.name}</span>
                    <span className={styles.cardMeta}>{a.scans} {a.scans === 1 ? 'scan' : 'scans'}</span>
                    <span className={styles.cardMeta}>{formatAreaDistance(a.distance)} {compassWord(a.bearing)}</span>
                  </div>
                  <button type="button" className={styles.go} onClick={() => onGo(a.key)}>Go to {a.name}</button>
                </div>
              ) : (
                <button type="button" className={styles.pill} onClick={() => setOpen(a.key)} onFocus={() => setOpen(a.key)}>
                  <span className={styles.dot} style={{ background: a.colour }} />
                  <span className={styles.pillName}>{a.name}</span>
                  <span className={styles.pillDist}>{formatAreaDistance(a.distance)}</span>
                </button>
              )}
            </div>
          </div>
        );
      })}

      {left.length > 0 && <div className={`${styles.edge} ${styles.edgeLeft}`}>{left.slice(0, EDGE_MAX).map((a) => chip(a, 'left'))}{more(left)}</div>}
      {right.length > 0 && <div className={`${styles.edge} ${styles.edgeRight}`}>{right.slice(0, EDGE_MAX).map((a) => chip(a, 'right'))}{more(right)}</div>}
      {behind.length > 0 && <div className={`${styles.edge} ${styles.edgeBehind}`}>{behind.slice(0, EDGE_MAX).map((a) => chip(a, 'behind'))}{more(behind)}</div>}
    </div>
  );
};

export default TourHorizon;
